import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/apiClient";
import { V1 } from "@/constants/testIds";

const RECENT_KEY = "rentopc.search.recent";
const MAX_RECENT = 8;

const TYPE_ROUTE = {
  customer: (id) => `/admin/cases?customer=${id}`,
  passport: (id) => `/admin/inventory/${id}`,
  sales: (id) => `/admin/sales/${id}`,
  purchase: (id) => `/admin/purchases/${id}`,
  vendor: () => `/admin/vendors`,
  warranty: (id) => `/admin/warranties/${id}`,
  case: (id) => `/admin/cases/${id}`,
  campaign: (id) => `/admin/campaigns/${id}`,
};

const TYPE_LABEL = {
  customer: "Customer", passport: "Passport", sales: "Sales Order",
  purchase: "Purchase", vendor: "Vendor", warranty: "Warranty",
  case: "Case", campaign: "Campaign",
};

// Facet chips — map a UI label to the backend `types` filter (comma-separated).
// "all" means: no filter (server decides based on the caller's role scope).
const FACETS = [
  { key: "all",      label: "All",        types: null },
  { key: "passport", label: "Passport",   types: "passport" },
  { key: "purchase", label: "Purchase",   types: "purchase" },
  { key: "vendor",   label: "Vendor",     types: "vendor" },
  { key: "sales",    label: "Sales",      types: "sales" },
  { key: "customer", label: "Customer",   types: "customer" },
  { key: "warranty", label: "Warranty",   types: "warranty" },
  { key: "case",     label: "Case",       types: "case" },
];

// Passport UID pattern: e.g. RP2608020001 (RP + 10 digits).
// Loose enough to catch partial typing (RP + at least 4 digits).
const PASSPORT_UID_RE = /^rp\d{4,}$/i;

// A short catalogue of what the user can type. Shown below the input
// so the operator learns which identifiers are recognised.
const HINTS = [
  { label: "Passport UID",        example: "RP2608020001" },
  { label: "Legacy Passport No",  example: "LP-000123" },
  { label: "Mfr Serial",          example: "PF3ABC12" },
  { label: "Internal Barcode",    example: "BC-987654" },
  { label: "Purchase No",         example: "PO-2025-042" },
  { label: "Vendor Return No",    example: "VR-000017" },
  { label: "Vendor Name",         example: "Ingram Micro" },
  { label: "Laptop Model",        example: "ThinkPad T14" },
];

function loadRecents() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
  catch { return []; }
}
function saveRecent(q) {
  if (!q || q.length < 2) return;
  const cur = loadRecents().filter((x) => x !== q);
  cur.unshift(q);
  localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, MAX_RECENT)));
}

export default function GlobalSearchModal({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState(loadRecents());
  const [focus, setFocus] = useState(0);
  const [facet, setFacet] = useState("all");
  const [autoFacet, setAutoFacet] = useState(true); // auto-lock facet from query pattern
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const run = useCallback(async (query, activeFacet) => {
    if (!query || query.length < 2) { setItems([]); return; }
    setLoading(true);
    try {
      const facetDef = FACETS.find((f) => f.key === activeFacet) || FACETS[0];
      const params = { q: query, limit: 20 };
      if (facetDef.types) params.types = facetDef.types;
      const r = await api.get("/search", { params });
      setItems(r.data.items || []);
      setFocus(0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setQ("");
      setItems([]);
      setFacet("all");
      setAutoFacet(true);
      setRecents(loadRecents());
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Auto-detect: if the query looks like a Passport UID (RP + digits),
  // narrow the search to passport results. Only when the user hasn't
  // explicitly changed the facet themselves.
  useEffect(() => {
    if (!autoFacet) return;
    const trimmed = q.trim();
    if (PASSPORT_UID_RE.test(trimmed)) {
      setFacet("passport");
    } else if (facet === "passport" && !PASSPORT_UID_RE.test(trimmed)) {
      // Reset back to "all" once the user is no longer typing a UID.
      setFacet("all");
    }
  }, [q, autoFacet, facet]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => run(q, facet), 220);
    return () => clearTimeout(debounceRef.current);
  }, [q, facet, open, run]);

  const pick = useCallback((it) => {
    saveRecent(q);
    const route = TYPE_ROUTE[it.type];
    if (route) navigate(route(it.id));
    onClose();
  }, [navigate, onClose, q]);

  const onKey = (e) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocus((f) => Math.min(f + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocus((f) => Math.max(f - 1, 0));
    } else if (e.key === "Enter" && items[focus]) {
      e.preventDefault();
      pick(items[focus]);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it) => { (g[it.type] ||= []).push(it); });
    return g;
  }, [items]);

  // Highlight which token of a result matched the query (case-insensitive).
  const highlight = useCallback((text) => {
    const needle = q.trim();
    if (!text || !needle || needle.length < 2) return text;
    try {
      const safe = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`(${safe})`, "ig");
      const parts = String(text).split(re);
      return parts.map((p, i) =>
        re.test(p) && p.toLowerCase() === needle.toLowerCase()
          ? <mark key={i} className="bg-yellow-200 text-slate-900 rounded-sm px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      );
    } catch {
      return text;
    }
  }, [q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center pt-20 px-4"
      onClick={onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center border-b border-slate-200 px-4">
          <Search size={16} className="text-slate-400" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            data-testid={V1.searchInput}
            placeholder="Passport UID · Legacy No · Serial · Barcode · PO · Vendor · Model…"
            className="flex-1 px-3 py-3 text-sm outline-none" />
          {loading && <div className="text-xs text-slate-400">Searching…</div>}
          <button onClick={onClose} data-testid={V1.searchClose}
            className="ml-2 text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>

        {/* Facet chips — narrow the search to one entity type */}
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-3 py-2 bg-slate-50">
          {FACETS.map((f) => {
            const active = facet === f.key;
            return (
              <button
                key={f.key}
                type="button"
                data-testid={`search-facet-${f.key}`}
                onClick={() => { setAutoFacet(false); setFacet(f.key); }}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="max-h-96 overflow-auto">
          {q.length < 2 && (
            <div className="p-3 space-y-3">
              {/* What the operator can search by */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pb-1.5">
                  Search by
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-2">
                  {HINTS.map((h) => (
                    <button
                      key={h.label}
                      type="button"
                      onClick={() => setQ(h.example)}
                      className="flex items-center justify-between text-left px-2 py-1 rounded hover:bg-slate-50"
                    >
                      <span className="text-xs text-slate-600">{h.label}</span>
                      <code className="text-[10px] font-mono text-slate-400 group-hover:text-slate-700">
                        {h.example}
                      </code>
                    </button>
                  ))}
                </div>
              </div>

              {recents.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pb-1.5">
                    Recent searches
                  </div>
                  <ul>
                    {recents.map((r, i) => (
                      <li key={r}>
                        <button data-testid={V1.searchRecent(i)}
                          onClick={() => setQ(r)}
                          className="w-full text-left px-3 py-2 rounded hover:bg-slate-50 text-sm">
                          <Search size={12} className="inline mr-2 text-slate-400" />{r}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {q.length >= 2 && items.length === 0 && !loading && (
            <div className="p-10 text-center text-sm text-slate-400">
              No results for &quot;{q}&quot;
              {facet !== "all" && (
                <div className="mt-2 text-xs text-slate-400">
                  Filter is set to <span className="font-medium text-slate-600">
                    {FACETS.find((f) => f.key === facet)?.label}
                  </span>.{" "}
                  <button className="underline text-slate-500 hover:text-slate-800"
                    onClick={() => { setAutoFacet(false); setFacet("all"); }}>
                    Search all types
                  </button>
                </div>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="p-2">
              {Object.entries(grouped).map(([type, rows]) => (
                <div key={type} className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 px-3 py-1">
                    {TYPE_LABEL[type] || type} ({rows.length})
                  </div>
                  <ul>
                    {rows.map((it) => {
                      const idx = items.indexOf(it);
                      const active = idx === focus;
                      return (
                        <li key={`${it.type}-${it.id}`}>
                          <button data-testid={V1.searchResult(idx)}
                            onClick={() => pick(it)}
                            onMouseEnter={() => setFocus(idx)}
                            className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 text-sm ${
                              active ? "bg-slate-900 text-white" : "hover:bg-slate-50"
                            }`}>
                            <div className="flex-1 min-w-0">
                              <div className={`truncate ${active ? "text-white" : "text-slate-900"}`}>
                                {highlight(it.title)}
                              </div>
                              {it.subtitle && (
                                <div className={`truncate text-xs ${active ? "text-slate-300" : "text-slate-500"}`}>
                                  {highlight(it.subtitle)}
                                </div>
                              )}
                            </div>
                            <ArrowRight size={12} className={active ? "text-white" : "text-slate-400"} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500 flex justify-between">
          <span>↑↓ to navigate · Enter to open</span>
          <span>Esc to close</span>
        </div>
      </div>
    </div>
  );
}
